import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Car, Zap, Leaf, Battery, ArrowRight, Building2, Newspaper, ChevronRight, MapPin, Package, Users, Briefcase, Wallet, Gauge, Mountain, Fuel, Settings, Star, Heart, Shield, Sparkles, Clock, Search, ExternalLink, BookOpen } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { CarCardGridSkeleton } from '@/components/cars/CarCardSkeleton';
import { PublicLayout } from '@/components/layout/PublicLayout';
import { Seo } from '@/components/seo/Seo';

import { TipUsSection } from '@/components/home/TipUsSection';
import { WelcomeStrip } from '@/components/home/WelcomeStrip';
import { CampaignTakeover } from '@/components/home/CampaignTakeover';
import { hasSeenHomeCampaign, isHomeCampaignWindowOpen } from '@/config/homeCampaign';
import { getConsent } from '@/components/consent/CookieBanner';


import { TopAffiliateStrip } from '@/components/layout/TopAffiliateStrip';
import { HomePartnerStrip } from '@/components/home/HomePartnerStrip';
import { CarSearchAutocomplete } from '@/components/search/CarSearchAutocomplete';
import { QuickCarSearchBar } from '@/components/home/QuickCarSearchBar';

import { useNavigate } from 'react-router-dom';
import { formatPrice, formatNumber, formatDate } from '@/lib/formatters';
import { powertrainLabels, getPowertrainBadgeVariant } from '@/types/database';
import type { Tables } from '@/integrations/supabase/types';
import type { LucideIcon } from 'lucide-react';

type Brand = Tables<'brands'>;
type Model = Tables<'models'>;
type Variant = Tables<'variants'>;
type PowertrainSpecs = Tables<'powertrain_specs'>;
type PriceDK = Tables<'prices_dk'>;
type NewsPost = Tables<'news_posts'>;
type Campaign = Tables<'campaigns'> & { brands?: Brand | null };

interface FeaturedCar {
  variant: Variant;
  model: Model;
  brand: Brand;
  specs: PowertrainSpecs | null;
  lowestPrice: PriceDK | null;
}

interface InspirationFilter {
  id: string;
  label: string;
  icon: string;
  search_params: string;
}

const iconMap: Record<string, LucideIcon> = {
  Users, Briefcase, Wallet, Gauge, Mountain, Car, Zap, Battery, Fuel, Settings, Star, Heart, Shield, Sparkles
};

export default function Index() {
  const navigate = useNavigate();

  // Forside-kampagne: fuldskærms tilmeldingslag, én gang pr. browser i kampagneperioden.
  // Rendres oven på forsiden, så crawlere stadig ser det normale forsideindhold.
  // Vises først når cookie-samtykke er afgivet, så de to lag ikke dækker hinanden.
  const [showCampaign, setShowCampaign] = useState(false);
  useEffect(() => {
    if (!isHomeCampaignWindowOpen() || hasSeenHomeCampaign()) return;
    if (getConsent()) {
      setShowCampaign(true);
      return;
    }
    const onConsent = () => setShowCampaign(true);
    window.addEventListener('consent:change', onConsent);
    return () => window.removeEventListener('consent:change', onConsent);
  }, []);




  const handleSearch = (query: string) => {
    navigate(`/biler?q=${encodeURIComponent(query)}`);
  };

  // Fetch featured cars (latest 6)
  const { data: featuredCars = [], isLoading: carsLoading } = useQuery({
    queryKey: ['featured-cars'],
    queryFn: async (): Promise<FeaturedCar[]> => {
      const { data, error } = await supabase
        .from('variants')
        .select(`
          *,
          models!inner (*, brands!inner (*)),
          powertrain_specs (*),
          prices_dk (*)
        `)
        .eq('is_active', true)
        .order('updated_at', { ascending: false })
        .limit(6);

      if (error) throw error;

      return (data || []).map((variant: any) => ({
        variant,
        model: variant.models,
        brand: variant.models.brands,
        specs: variant.powertrain_specs,
        lowestPrice: variant.prices_dk?.length > 0
          ? variant.prices_dk.reduce((min: PriceDK, p: PriceDK) => p.price_dkk < min.price_dkk ? p : min, variant.prices_dk[0])
          : null,
      }));
    },
  });

  // Fetch upcoming cars
  const { data: upcomingCars = [], isLoading: upcomingLoading } = useQuery({
    queryKey: ['upcoming-cars'],
    queryFn: async (): Promise<FeaturedCar[]> => {
      const { data, error } = await supabase
        .from('variants')
        .select(`
          *,
          models!inner (*, brands!inner (*)),
          powertrain_specs (*),
          prices_dk (*)
        `)
        .eq('is_active', true)
        .eq('availability_status', 'upcoming')
        .order('expected_launch_date', { ascending: true, nullsFirst: false })
        .limit(6);

      if (error) throw error;

      return (data || []).map((variant: any) => ({
        variant,
        model: variant.models,
        brand: variant.models.brands,
        specs: variant.powertrain_specs,
        lowestPrice: variant.prices_dk?.length > 0
          ? variant.prices_dk.reduce((min: PriceDK, p: PriceDK) => p.price_dkk < min.price_dkk ? p : min, variant.prices_dk[0])
          : null,
      }));
    },
  });

  // Fetch brands
  const { data: brands = [], isLoading: brandsLoading } = useQuery({
    queryKey: ['brands-home'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('brands')
        .select('*')
        .order('name')
        .limit(8);
      if (error) throw error;
      return data as Brand[];
    },
  });

  // Fetch latest news
  const { data: latestNews = [], isLoading: newsLoading } = useQuery({
    queryKey: ['latest-news'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('news_posts')
        .select('*')
        .eq('is_published', true)
        .order('published_at', { ascending: false })
        .limit(3);
      if (error) throw error;
      return data as NewsPost[];
    },
  });

  // Fetch inspiration filters from database
  const { data: inspirationFilters = [] } = useQuery({
    queryKey: ['inspiration-filters'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('inspiration_filters')
        .select('*')
        .eq('is_active', true)
        .order('sort_order');
      if (error) throw error;
      return data as InspirationFilter[];
    },
  });

  // Fetch featured landing pages (guides)
  const { data: featuredGuides = [], isLoading: guidesLoading } = useQuery({
    queryKey: ['featured-guides'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('landing_pages')
        .select('slug, title, heading, intro_md')
        .eq('is_active', true)
        .order('sort_order', { ascending: true })
        .limit(6);
      if (error) throw error;
      return data as { slug: string; title: string; heading: string; intro_md: string | null }[];
    },
  });

  // Fetch active campaigns (kun inden for kampagneperioden)
  const { data: campaigns = [], isLoading: campaignsLoading } = useQuery({
    queryKey: ['active-campaigns'],
    queryFn: async () => {
      const today = new Date().toISOString().slice(0, 10);
      const { data, error } = await supabase
        .from('campaigns')
        .select('*, brands(*)')
        .eq('is_active', true)
        .or(`start_date.is.null,start_date.lte.${today}`)
        .or(`end_date.is.null,end_date.gte.${today}`)
        .order('sort_order')
        .limit(4);
      if (error) throw error;
      return data as Campaign[];
    },
  });


  const quickFilters = [
    { label: 'Lang rækkevidde', icon: Zap, href: '/biler?minRange=500', color: 'bg-green-500' },
    { label: 'SUV', icon: Car, href: '/biler?bodyType=SUV', color: 'bg-primary' },
    { label: 'Familiebil', icon: Users, href: '/biler?minSeats=5', color: 'bg-sky-500' },
    { label: 'Hurtig opladning', icon: Zap, href: '/biler?minChargingDcKw=150', color: 'bg-amber-500' },
  ];

  return (
    <PublicLayout>
      <Seo
        title="Min Nye Elbil – alle nye elbiler, tests og nyheder samlet"
        description="Vi samler alle nye elbiler fra bilmærkernes hjemmesider, danske tests og nyheder ét sted. Find priser, rækkevidde, udstyr og anmeldelser."
        path="/"
      />
      {showCampaign && <CampaignTakeover onClose={() => setShowCampaign(false)} />}
      {/* På desktop møder man velkomst + nyhedsbrev øverst.
          På mobil er hero'en det første — velkomst og notits ligger længere nede. */}
      <div className="hidden lg:block">

        <WelcomeStrip />

        {/* Diskret notits — vi opfordrer altid til at verificere data hos bilmærket */}
        <div className="bg-muted/40 border-b border-border/60">
          <div className="container mx-auto px-4 py-2 flex items-start gap-2 text-xs text-muted-foreground">
            <ExternalLink className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
            <p>
              Vi gør vores bedste for at holde data opdateret, men tjek altid de endelige
              specifikationer og priser direkte hos bilmærket. Du finder links til de officielle
              danske sider på hvert mærke og hver bil.
            </p>
          </div>
        </div>
      </div>


      {/* Hero Section */}
      <section className="hero-gradient text-primary-foreground py-8 md:py-16 lg:py-20 relative z-30">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center animate-fade-in mb-6 md:mb-10">
            <h1 className="font-display text-2xl md:text-4xl lg:text-5xl font-bold mb-3 md:mb-4">
              Find din næste elbil
            </h1>
            <p className="text-sm md:text-lg text-primary-foreground/90 mb-4 md:mb-5">
              Vælg den måde, der passer dig — søg, filtrér eller lad os hjælpe dig hele vejen.
            </p>
            <Button
              asChild
              variant="outline"
              size="sm"
              className="bg-transparent border-primary-foreground/40 text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground gap-1.5"
            >
              <Link to="/nyheder">
                <Newspaper className="w-4 h-4" />
                Se seneste nyheder
              </Link>
            </Button>
          </div>

          {/* Hurtig søgning - over de tre indgange */}
          <div className="max-w-5xl mx-auto mb-6 md:mb-8">
            <QuickCarSearchBar />
          </div>

          {/* 3 indgange */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-5 max-w-6xl mx-auto relative z-30">

            {/* 1) Fritekstsøgning */}
            <div className="relative z-40 bg-card text-foreground rounded-2xl p-5 md:p-6 shadow-xl flex flex-col opacity-0 animate-fade-in motion-reduce:opacity-100 motion-reduce:animate-none" style={{ animationDelay: '0ms', animationDuration: '500ms', animationFillMode: 'forwards' }}>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-primary text-primary-foreground flex items-center justify-center shrink-0">
                  <Search className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">1. Fritekst</p>
                  <h2 className="font-display font-bold text-lg leading-tight">Søg efter mærke eller model</h2>
                </div>
              </div>
              <p className="text-sm text-muted-foreground mb-4">
                Ved du allerede hvilken elbil du vil se? Skriv f.eks. "Tesla Model Y" eller "Skoda Enyaq".
              </p>
              <div className="mt-auto">
                <CarSearchAutocomplete
                  onSearch={handleSearch}
                  inputClassName="h-12 text-base bg-background text-foreground border-2 border-primary/40 placeholder:text-foreground/70 placeholder:font-medium focus-visible:ring-2 focus-visible:ring-primary focus-visible:border-primary shadow-sm"
                  autoFocus={false}
                />
              </div>
            </div>

            {/* 2) Find elbil (filtre) */}
            <div className="bg-card text-foreground rounded-2xl p-5 md:p-6 shadow-xl flex flex-col opacity-0 animate-fade-in motion-reduce:opacity-100 motion-reduce:animate-none" style={{ animationDelay: '180ms', animationDuration: '500ms', animationFillMode: 'forwards' }}>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-accent text-accent-foreground flex items-center justify-center shrink-0">
                  <Car className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">2. Find elbil</p>
                  <h2 className="font-display font-bold text-lg leading-tight">Filtrér efter dine behov</h2>
                </div>
              </div>
              <p className="text-sm text-muted-foreground mb-4">
                Brug konkrete filtre — pris, rækkevidde, sæder, karrosseri — og se alle elbiler, der matcher.
              </p>
              <div className="mt-auto">
                <Button asChild className="w-full gap-2" size="lg">
                  <Link to="/biler">
                    Filtre
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </Button>
              </div>
            </div>

            {/* 3) Elbilhjælp (quiz) */}
            <div className="bg-card text-foreground rounded-2xl p-5 md:p-6 shadow-xl flex flex-col ring-2 ring-emerald-500/40 opacity-0 animate-fade-in motion-reduce:opacity-100 motion-reduce:animate-none" style={{ animationDelay: '360ms', animationDuration: '500ms', animationFillMode: 'forwards' }}>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-emerald-600 text-white flex items-center justify-center shrink-0">
                  <Sparkles className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-emerald-700 dark:text-emerald-400">3. Elbilhjælp</p>
                  <h2 className="font-display font-bold text-lg leading-tight">Lad os finde den rigtige til dig</h2>
                </div>
              </div>
              <p className="text-sm text-muted-foreground mb-4">
                Svar på 9 hurtige spørgsmål — vi rangerer elbilerne i vores database efter dit behov.
              </p>
              <div className="mt-auto">
                <Button asChild className="w-full bg-emerald-600 hover:bg-emerald-700 text-white gap-2" size="lg">
                  <Link to="/find-bil">
                    Elbilhjælperen
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>

          {/* Inspiration Filters */}
          {inspirationFilters.length > 0 && (
            <div className="mt-6 md:mt-8 text-center">
              <p className="text-xs md:text-sm text-primary-foreground/60 mb-3">Bliv inspireret:</p>
              <div className="flex flex-wrap justify-center gap-2">
                {inspirationFilters.map((filter) => {
                  const IconComponent = iconMap[filter.icon] || Sparkles;
                  return (
                    <Link
                      key={filter.id}
                      to={`/biler?${filter.search_params}`}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-primary-foreground/10 hover:bg-primary-foreground/20 text-primary-foreground text-xs md:text-sm transition-colors"
                    >
                      <IconComponent className="w-3.5 h-3.5" />
                      {filter.label}
                    </Link>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Mobil: kort notits + annonceplads efter hero, så toppen er ren */}
      <div className="lg:hidden bg-muted/40 border-b border-border/60">
        <div className="container mx-auto px-4 py-2 flex items-center gap-2 text-xs text-muted-foreground">
          <ExternalLink className="w-3.5 h-3.5 flex-shrink-0" />
          <p>Tjek altid endelige priser og specifikationer hos bilmærket.</p>
        </div>
      </div>
      <div className="lg:hidden">
        <TopAffiliateStrip />
      </div>





      {/* Latest News - Magazine layout */}
      {(newsLoading || latestNews.length > 0) && (
        <section className="py-12 bg-background">
          <div className="container mx-auto px-4">
            <div className="flex items-end justify-between mb-6 border-b border-border/60 pb-3">
              <div className="flex items-center gap-3">
                <Newspaper className="w-5 h-5 text-primary" />
                <h2 className="font-display text-xl md:text-2xl font-bold tracking-tight">
                  Seneste nyheder
                </h2>
              </div>
              <Button asChild variant="ghost" size="sm" className="gap-1">
                <Link to="/nyheder">
                  Alle nyheder <ArrowRight className="w-4 h-4" />
                </Link>
              </Button>
            </div>

            {newsLoading ? (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 bg-card rounded-xl overflow-hidden">
                  <Skeleton className="aspect-[16/10]" />
                  <div className="p-5 space-y-3">
                    <Skeleton className="h-4 w-24" />
                    <Skeleton className="h-8 w-3/4" />
                    <Skeleton className="h-4 w-full" />
                  </div>
                </div>
                <div className="space-y-4">
                  {[...Array(2)].map((_, i) => (
                    <div key={i} className="flex gap-3 bg-card rounded-xl overflow-hidden p-3">
                      <Skeleton className="w-28 h-28 rounded-lg flex-shrink-0" />
                      <div className="flex-1 space-y-2">
                        <Skeleton className="h-3 w-20" />
                        <Skeleton className="h-5 w-full" />
                        <Skeleton className="h-4 w-2/3" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              (() => {
                const [featured, ...rest] = latestNews;
                return (
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Featured */}
                    <Link
                      to={`/nyheder/${featured.slug}`}
                      className="lg:col-span-2 group block bg-card rounded-xl overflow-hidden shadow-card card-hover"
                    >
                      <div className="aspect-[16/10] bg-muted overflow-hidden relative">
                        {featured.cover_image_url ? (
                          <img
                            src={featured.cover_image_url}
                            alt={featured.title}
                            width={960}
                            height={600}
                            loading="lazy"
                            decoding="async"
                            className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Newspaper className="w-16 h-16 text-muted-foreground/40" />
                          </div>
                        )}
                        <span className="absolute top-3 left-3 bg-primary text-primary-foreground text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded">
                          Nyt
                        </span>
                      </div>
                      <div className="p-5 md:p-6">
                        {featured.published_at && (
                          <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-2">
                            {formatDate(featured.published_at)}
                          </p>
                        )}
                        <h3 className="font-display font-bold text-xl md:text-2xl leading-tight mb-3 group-hover:text-primary transition-colors">
                          {featured.title}
                        </h3>
                        {featured.excerpt && (
                          <p className="text-sm md:text-base text-muted-foreground line-clamp-3">
                            {featured.excerpt}
                          </p>
                        )}
                      </div>
                    </Link>

                    {/* Side stack */}
                    <div className="flex flex-col gap-4">
                      {rest.slice(0, 2).map((post) => (
                        <Link
                          key={post.id}
                          to={`/nyheder/${post.slug}`}
                          className="group flex gap-3 bg-card rounded-xl overflow-hidden p-3 border border-border/60 hover:border-primary/40 transition-colors"
                        >
                          <div className="w-28 h-28 flex-shrink-0 rounded-lg bg-muted overflow-hidden">
                            {post.cover_image_url ? (
                              <img
                                src={post.cover_image_url}
                                alt={post.title}
                                width={224}
                                height={224}
                                loading="lazy"
                                decoding="async"
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <Newspaper className="w-6 h-6 text-muted-foreground/50" />
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0 flex flex-col justify-center">
                            {post.published_at && (
                              <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">
                                {formatDate(post.published_at)}
                              </p>
                            )}
                            <h4 className="font-display font-semibold text-sm md:text-base leading-snug line-clamp-3 group-hover:text-primary transition-colors">
                              {post.title}
                            </h4>
                          </div>
                        </Link>
                      ))}
                      <Link
                        to="/nyheder"
                        className="text-sm text-primary hover:underline inline-flex items-center gap-1 self-start mt-1"
                      >
                        Se alle nyheder <ArrowRight className="w-4 h-4" />
                      </Link>
                    </div>
                  </div>
                );
              })()
            )}
          </div>
        </section>
      )}

      {/* Featured Guides */}
      {(guidesLoading || featuredGuides.length > 0) && (
        <section className="py-12 bg-muted/50">
          <div className="container mx-auto px-4">
            <div className="flex items-end justify-between mb-6 border-b border-border/60 pb-3">
              <div className="flex items-center gap-3">
                <BookOpen className="w-5 h-5 text-primary" />
                <h2 className="font-display text-xl md:text-2xl font-bold tracking-tight">
                  Guides til elbilen
                </h2>
              </div>
              <Button asChild variant="ghost" size="sm" className="gap-1">
                <Link to="/guides">
                  Alle guides <ArrowRight className="w-4 h-4" />
                </Link>
              </Button>
            </div>

            {guidesLoading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[...Array(3)].map((_, i) => (
                  <Skeleton key={i} className="h-32" />
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {featuredGuides.map((guide) => (
                  <Link
                    key={guide.slug}
                    to={`/guides/${guide.slug}`}
                    className="group block bg-card rounded-xl border border-border/60 hover:border-primary/40 transition-colors p-5"
                  >
                    <h3 className="font-display font-semibold text-lg group-hover:text-primary transition-colors mb-2">
                      {guide.heading || guide.title}
                    </h3>
                    {guide.intro_md && (
                      <p className="text-sm text-muted-foreground line-clamp-2">
                        {guide.intro_md.replace(/[#*_\[\]\(\)]/g, '').slice(0, 155)}
                      </p>
                    )}
                  </Link>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Anbefalede partnere (affiliate) */}
      <HomePartnerStrip />

      {/* Tip os */}
      <TipUsSection />

      {/* Campaigns Section */}
      {(campaignsLoading || campaigns.length > 0) && (
        <section className="py-12 bg-muted/50">
          <div className="container mx-auto px-4">
            <div className="flex items-center justify-between mb-8">
              <h2 className="font-display text-xl md:text-2xl font-bold">Kampagner</h2>
              <Button asChild variant="ghost" className="gap-1">
                <Link to="/kampagner">
                  Alle kampagner <ArrowRight className="w-4 h-4" />
                </Link>
              </Button>
            </div>

            {campaignsLoading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="bg-card rounded-xl overflow-hidden">
                    <Skeleton className="aspect-[16/9]" />
                    <div className="p-4 space-y-3">
                      <Skeleton className="h-4 w-24" />
                      <Skeleton className="h-6 w-full" />
                      <Skeleton className="h-4 w-3/4" />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {campaigns.map((campaign) => {
                  const CardWrapper = campaign.link_url ? 'a' : 'div';
                  const cardProps = campaign.link_url ? { 
                    href: campaign.link_url, 
                    target: '_blank', 
                    rel: 'noopener noreferrer' 
                  } : {};
                  
                  return (
                    <CardWrapper
                      key={campaign.id}
                      {...cardProps}
                      className={`block bg-card rounded-xl shadow-card overflow-hidden ${campaign.link_url ? 'card-hover cursor-pointer' : ''} group`}
                    >
                      <div className="aspect-[16/9] bg-muted overflow-hidden relative">
                        {campaign.cover_image_url ? (
                          <img
                            src={campaign.cover_image_url}
                            alt={campaign.title}
                            width={640}
                            height={360}
                            loading="lazy"
                            decoding="async"
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary/20 to-accent/20">
                            <Zap className="w-12 h-12 text-primary/40" />
                          </div>
                        )}
                        {campaign.brands && (
                          <Badge className="absolute top-3 left-3" variant="secondary">
                            {campaign.brands.name}
                          </Badge>
                        )}
                      </div>

                      <div className="p-4">
                        <h3 className="font-display font-semibold text-lg leading-tight mb-2 group-hover:text-primary transition-colors">
                          {campaign.title}
                        </h3>
                        {campaign.description && (
                          <p className="text-sm text-muted-foreground line-clamp-2">
                            {campaign.description}
                          </p>
                        )}
                        {campaign.end_date && (
                          <p className="text-xs text-muted-foreground mt-2">
                            Gælder til {formatDate(campaign.end_date)}
                          </p>
                        )}
                      </div>
                    </CardWrapper>
                  );
                })}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Quick Filters */}
      <section className="py-12 bg-muted/50">
        <div className="container mx-auto px-4">
          <h2 className="font-display text-xl md:text-2xl font-bold text-center mb-8">Populære kategorier</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
            {quickFilters.map((filter, i) => (
              <Link
                key={filter.label}
                to={filter.href}
                className="card-hover bg-card rounded-xl p-6 text-center group animate-slide-up shadow-card"
                style={{ animationDelay: `${i * 100}ms` }}
              >
                <div className={`w-14 h-14 ${filter.color} rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform`}>
                  <filter.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="font-semibold text-foreground">{filter.label}</h3>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Upcoming Cars */}
      {(upcomingLoading || upcomingCars.length > 0) && (
        <section className="py-16 bg-background">
          <div className="container mx-auto px-4">
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-amber-500 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-white" />
                </div>
                <h2 className="font-display text-xl md:text-2xl font-bold">Kommende elbiler</h2>
              </div>
              <Button asChild variant="ghost" className="gap-1">
                <Link to="/biler?showUpcoming=only">
                  Se alle <ArrowRight className="w-4 h-4" />
                </Link>
              </Button>
            </div>

            {upcomingLoading ? (
              <CarCardGridSkeleton count={3} />
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {upcomingCars.map((car) => {
                  const isEV = true;
                  const isHybrid = false;

                  return (
                    <Link
                      key={car.variant.id}
                      to={`/biler/${car.brand.slug}/${car.model.slug}/${car.variant.slug}`}
                      className="block bg-card rounded-xl shadow-card overflow-hidden card-hover group"
                    >
                      <div className="aspect-[16/10] bg-muted relative overflow-hidden">
                        {car.model.image_url ? (
                          <img
                            src={car.model.image_url}
                            alt={`${car.brand.name} ${car.model.name}`}
                            width={640}
                            height={400}
                            loading="lazy"
                            decoding="async"
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Car className="w-16 h-16 text-muted-foreground/60" />
                          </div>
                        )}
                        <div className="absolute top-3 left-3 flex gap-1.5">
                          <Badge variant="upcoming" className="gap-1">
                            <Clock className="w-3 h-3" />
                            Kommer snart
                          </Badge>
                          <Badge
                            variant={getPowertrainBadgeVariant(car.variant.powertrain_type)}
                          >
                            {powertrainLabels[car.variant.powertrain_type]}
                          </Badge>
                        </div>
                      </div>

                      <div className="p-4 space-y-3">
                        <div>
                          <p className="text-sm text-muted-foreground">{car.brand.name}</p>
                          <h3 className="font-display font-semibold text-lg leading-tight">
                            {car.model.name} <span className="text-primary">{car.variant.name}</span>
                          </h3>
                        </div>

                        <div className="flex flex-wrap gap-2 text-sm text-muted-foreground">
                          {car.specs?.power_hp && (
                            <span className="flex items-center gap-1">
                              <Zap className="w-3.5 h-3.5" />
                              {car.specs.power_hp} hk
                            </span>
                          )}
                          {(isEV || isHybrid) && car.specs?.range_wltp_km && (
                            <span className="flex items-center gap-1">
                              <MapPin className="w-3.5 h-3.5" />
                              {formatNumber(car.specs.range_wltp_km)} km
                            </span>
                          )}
                          {car.variant.expected_launch_date && (
                            <span className="flex items-center gap-1 text-amber-600">
                              <Clock className="w-3.5 h-3.5" />
                              {new Date(car.variant.expected_launch_date).toLocaleDateString('da-DK', { month: 'short', year: 'numeric' })}
                            </span>
                          )}
                        </div>

                        <div className="pt-2 border-t border-border">
                          {car.variant.expected_price_dkk ? (
                            <div>
                              <p className="text-xs text-muted-foreground">Forventet pris</p>
                              <p className="font-display font-bold text-xl text-primary">
                                {formatPrice(car.variant.expected_price_dkk)}
                              </p>
                            </div>
                          ) : car.lowestPrice ? (
                            <div>
                              <p className="text-xs text-muted-foreground">Forventet pris</p>
                              <p className="font-display font-bold text-xl text-primary">
                                {formatPrice(car.lowestPrice.price_dkk)}
                              </p>
                            </div>
                          ) : (
                            <p className="text-muted-foreground text-sm">Pris oplyses senere</p>
                          )}
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Featured Cars */}
      <section className="py-16 bg-muted/50">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between mb-8">
            <h2 className="font-display text-xl md:text-2xl font-bold">Seneste elbiler</h2>
            <Button asChild variant="ghost" className="gap-1">
              <Link to="/biler">
                Se alle <ArrowRight className="w-4 h-4" />
              </Link>
            </Button>
          </div>

          {carsLoading ? (
            <CarCardGridSkeleton count={6} />
          ) : featuredCars.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredCars.map((car) => {
                const isEV = true;
                const isHybrid = false;

                return (
                  <Link
                    key={car.variant.id}
                    to={`/biler/${car.brand.slug}/${car.model.slug}/${car.variant.slug}`}
                    className="block bg-card rounded-xl shadow-card overflow-hidden card-hover group"
                  >
                    <div className="aspect-[16/10] bg-muted relative overflow-hidden">
                      {car.model.image_url ? (
                        <img
                          src={car.model.image_url}
                          alt={`${car.brand.name} ${car.model.name}`}
                          width={640}
                          height={400}
                          loading="lazy"
                          decoding="async"
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Car className="w-16 h-16 text-muted-foreground/60" />
                        </div>
                      )}
                      <Badge
                        variant={getPowertrainBadgeVariant(car.variant.powertrain_type)}
                        className="absolute top-3 left-3"
                      >
                        {powertrainLabels[car.variant.powertrain_type]}
                      </Badge>
                    </div>

                    <div className="p-4 space-y-3">
                      <div>
                        <p className="text-sm text-muted-foreground">{car.brand.name}</p>
                        <h3 className="font-display font-semibold text-lg leading-tight">
                          {car.model.name} <span className="text-primary">{car.variant.name}</span>
                        </h3>
                      </div>

                      <div className="flex flex-wrap gap-2 text-sm text-muted-foreground">
                        {car.specs?.power_hp && (
                          <span className="flex items-center gap-1">
                            <Zap className="w-3.5 h-3.5" />
                            {car.specs.power_hp} hk
                          </span>
                        )}
                        {(isEV || isHybrid) && car.specs?.range_wltp_km && (
                          <span className="flex items-center gap-1">
                            <MapPin className="w-3.5 h-3.5" />
                            {formatNumber(car.specs.range_wltp_km)} km
                          </span>
                        )}
                        {car.variant.trunk_l && (
                          <span className="flex items-center gap-1">
                            <Package className="w-3.5 h-3.5" />
                            {formatNumber(car.variant.trunk_l)} L
                          </span>
                        )}
                      </div>

                      <div className="pt-2 border-t border-border">
                        {car.lowestPrice ? (
                          <div>
                            <p className="text-xs text-muted-foreground">Fra</p>
                            <p className="font-display font-bold text-xl text-primary">
                              {formatPrice(car.lowestPrice.price_dkk)}
                            </p>
                          </div>
                        ) : (
                          <p className="text-muted-foreground text-sm">Pris ikke tilgængelig</p>
                        )}
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          ) : (
            <div className="bg-card rounded-xl shadow-card p-12 text-center">
              <Car className="w-16 h-16 text-muted-foreground/60 mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">Ingen elbiler endnu</h3>
              <p className="text-muted-foreground">
                Der er endnu ikke tilføjet nogen elbiler til databasen.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Brands Section */}
      <section className="py-16 bg-background">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between mb-8">
            <h2 className="font-display text-xl md:text-2xl font-bold">Udforsk mærker</h2>
            <Button asChild variant="ghost" className="gap-1">
              <Link to="/maerker">
                Alle mærker <ArrowRight className="w-4 h-4" />
              </Link>
            </Button>
          </div>

          {brandsLoading ? (
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-4">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="bg-card rounded-xl p-4 text-center">
                  <Skeleton className="w-12 h-12 rounded-lg mx-auto mb-2" />
                  <Skeleton className="h-4 w-16 mx-auto" />
                </div>
              ))}
            </div>
          ) : brands.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-4">
              {brands.map((brand) => (
                <Link
                  key={brand.id}
                  to={`/maerker/${brand.slug}`}
                  className="bg-card rounded-xl shadow-card p-4 text-center card-hover group"
                >
                  <div className="w-12 h-12 rounded-lg bg-muted flex items-center justify-center mx-auto mb-2 overflow-hidden">
                    {brand.logo_url ? (
                      <img
                        src={brand.logo_url}
                        alt={brand.name}
                        width={32}
                        height={32}
                        loading="lazy"
                        decoding="async"
                        className="w-8 h-8 object-contain"
                      />
                    ) : (
                      <Building2 className="w-6 h-6 text-muted-foreground" />
                    )}
                  </div>
                  <p className="font-medium text-sm group-hover:text-primary transition-colors truncate">
                    {brand.name}
                  </p>
                </Link>
              ))}
            </div>
          ) : (
            <div className="bg-card rounded-xl shadow-card p-8 text-center">
              <Building2 className="w-12 h-12 text-muted-foreground/60 mx-auto mb-3" />
              <p className="text-muted-foreground">Ingen mærker endnu</p>
            </div>
          )}
        </div>
      </section>

      {/* Mobil: velkomst, nyhedsbrev og feedback ligger her i stedet for i toppen */}
      <div className="lg:hidden">
        <WelcomeStrip />
      </div>



      {/* CTA Section */}
      <section className="py-16 bg-primary text-primary-foreground">
        <div className="container mx-auto px-4 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">Klar til at finde din drømme-elbil?</h2>
          <p className="text-primary-foreground/90 mb-8 max-w-xl mx-auto">
            Udforsk vores komplette database med alle elbiler i Danmark
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button asChild variant="hero" size="lg" className="gap-2">
              <Link to="/biler">
                <Search className="w-4 h-4" />
                Søg alle elbiler
              </Link>
            </Button>
            <Button asChild variant="secondary" size="lg" className="gap-2">
              <Link to="/maerker">
                <Building2 className="w-4 h-4" />
                Udforsk mærker
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
